import React from 'react';
import ClientTimeline from './ClientTimeline';
import ClientCharts from './ClientCharts';
import MeetingSummaryModal from './MeetingSummaryModal';
import ObjectionChart from './ObjectionChart';
import ServicesSoldChart from './ServicesSoldChart';
import ClientDetailsSection from './ClientDetailsSection';
import ClientTasksSection from './ClientTasksSection';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import OpenAI from 'openai';

// Configurar OpenAI
const openai = new OpenAI({
  apiKey: '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',
  dangerouslyAllowBrowser: true
});

// Función para formatear fecha correctamente desde MySQL
const formatDateCorrectly = (dateString) => {
  if (!dateString) return '';
  
  try {
    // Las fechas de MySQL vienen como YYYY-MM-DD o YYYY-MM-DD HH:MM:SS
    let dateOnly;
    
    if (typeof dateString === 'string') {
      dateOnly = dateString.split('T')[0].split(' ')[0];
    } else if (dateString instanceof Date) {
      dateOnly = dateString.toISOString().split('T')[0];
    } else {
      return dateString.toString();
    }
    
    const [year, month, day] = dateOnly.split('-');
    
    // Retornar directamente en formato DD/MM/YYYY sin crear objeto Date
    return `${day.padStart(2, '0')}/${month.padStart(2, '0')}/${year}`;
  } catch (error) {
    console.error('Error al formatear fecha:', error);
    return dateString.toString();
  }
};

const ClientProfile = ({ client, onBack, onNewMeeting, onEditMeeting, onUpdateClient, canEdit }) => {
  const [showSummaryModal, setShowSummaryModal] = React.useState(false);
  const [selectedMeetingForSummary, setSelectedMeetingForSummary] = React.useState(null);
  const [isEditingName, setIsEditingName] = React.useState(false);
  const [newClientName, setNewClientName] = React.useState(client.name);
  const [showClientDetails, setShowClientDetails] = React.useState(false);
  const [showClientTasks, setShowClientTasks] = React.useState(false);
  const [showClientHistory, setShowClientHistory] = React.useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = React.useState(false);
  const [isExportingHistorial, setIsExportingHistorial] = React.useState(false);
  const timelineRef = React.useRef(null);
  const historialContainerRef = React.useRef(null);
  
  // Función para exportar el historial a PDF mediante captura de pantalla
  const exportHistorialToPDF = async () => {
    try {
      setIsExportingHistorial(true);
      
      // Obtener el contenedor del historial
      const historialContainer = document.querySelector('.mt-6 h3.text-lg.font-semibold.text-\\[\\#0061A8\\].mb-4').parentNode;
      
      // Crear un clon del contenedor para manipularlo sin afectar la UI
      const clone = historialContainer.cloneNode(true);
      
      // Aplicar estilos al clon para prepararlo para la captura
      clone.style.position = 'absolute';
      clone.style.left = '-9999px';
      clone.style.background = 'white';
      clone.style.width = '800px'; // Ancho fijo para el PDF
      clone.style.padding = '20px';
      
      // Ocultar botones en el clon
      const buttons = clone.querySelectorAll('button, a.text-sm.bg-\\[\\#A8D08D\\]');
      buttons.forEach(button => {
        button.style.display = 'none';
      });
      
      // Agregar el clon al documento para capturarlo
      document.body.appendChild(clone);
      
      // Crear un nuevo PDF con orientación vertical
      const pdf = new jsPDF('p', 'pt', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const margin = 40;
      
      // Función para agregar header
      const addHeader = () => {
        pdf.setFillColor(0, 97, 168);
        pdf.rect(0, 0, pdfWidth, 40, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(16);
        pdf.setFont('helvetica', 'bold');
        pdf.text('DOCTORWEB', margin, 25);
        pdf.setFontSize(10);
        pdf.setFont('helvetica', 'normal');
        pdf.text('Historial de Juntas', margin + 100, 25);
      };
      
      // Función para agregar footer
      const addFooter = (pageNum) => {
        pdf.setFillColor(0, 97, 168);
        pdf.rect(0, pdfHeight - 30, pdfWidth, 30, 'F');
        pdf.setTextColor(255, 255, 255);
        pdf.setFontSize(10);
        pdf.text(`DOCTORWEB - Página ${pageNum}`, margin, pdfHeight - 15);
        pdf.text(`Cliente: ${client.name}`, pdfWidth - 150, pdfHeight - 15);
      };
      
      // Capturar el clon con html2canvas
      const canvas = await html2canvas(clone, {
        scale: 2, // Mayor calidad
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      // Obtener la imagen del canvas
      const imgData = canvas.toDataURL('image/png');
      
      // Calcular la altura proporcional para mantener la relación de aspecto
      const imgWidth = pdfWidth - (margin * 2);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Calcular cuántas páginas necesitamos
      const pageCount = Math.ceil(imgHeight / (pdfHeight - 100));
      
      // Agregar la imagen al PDF, dividida en múltiples páginas si es necesario
      for (let i = 0; i < pageCount; i++) {
        if (i > 0) {
          pdf.addPage();
        }
        
        // Agregar header y footer a cada página
        addHeader();
        addFooter(i + 1);
        
        // Calcular la porción de la imagen a mostrar en esta página
        const sourceY = i * (canvas.height / pageCount);
        const sourceHeight = canvas.height / pageCount;
        
        // Agregar la porción de la imagen a la página actual
        pdf.addImage(
          imgData, 
          'PNG', 
          margin, 
          50, 
          imgWidth, 
          imgHeight / pageCount, 
          '', 
          'FAST',
          0,
          sourceY / canvas.height,
          1,
          sourceHeight / canvas.height
        );
      }
      
      // Eliminar el clon del documento
      document.body.removeChild(clone);
      
      // Guardar el PDF
      const fileName = `Historial_${client.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      alert('¡Historial exportado exitosamente a PDF!');
    } catch (error) {
      console.error('Error al exportar historial a PDF:', error);
      alert('Error al exportar el historial. Por favor, inténtalo de nuevo.');
    } finally {
      setIsExportingHistorial(false);
    }
  };

  const calculateTotalPatients = () => {
    return client.meetings.reduce((total, meeting) => total + meeting.patients, 0);
  };

  const calculateTotalContacts = () => {
    return client.meetings.reduce((total, meeting) => total + meeting.contactsGenerated, 0);
  };

  const calculateConversionRate = () => {
    const totalContacts = calculateTotalContacts();
    if (totalContacts === 0) return '0%';
    const rate = (calculateTotalPatients() / totalContacts) * 100;
    return `${rate.toFixed(1)}%`;
  };

  const handleViewSummary = (meeting) => {
    setSelectedMeetingForSummary({ ...meeting, clientName: client.name });
    setShowSummaryModal(true);
  };

  const handleSaveClientName = () => {
    if (newClientName.trim() !== '' && newClientName !== client.name) {
      onUpdateClient({ ...client, name: newClientName.trim() });
    }
    setIsEditingName(false);
  };

  const totalPatients = calculateTotalPatients();
  const totalContacts = calculateTotalContacts();
  const conversionRate = calculateConversionRate();

  const maxContactsPatients = Math.max(totalContacts, totalPatients);
  const contactsWidth = maxContactsPatients > 0 ? (totalContacts / maxContactsPatients) * 100 : 0;
  const patientsWidth = maxContactsPatients > 0 ? (totalPatients / maxContactsPatients) * 100 : 0;
  const conversionWidth = parseFloat(conversionRate) > 0 ? parseFloat(conversionRate) : 0;

  const lastMeeting = client.meetings.length > 0 ? client.meetings[client.meetings.length - 1] : null;

  // Función para generar análisis automático con OpenAI - TODOS LOS DATOS
  const generateAIAnalysis = async (clientData) => {
    try {
      // Crear resumen conciso de juntas para optimizar tokens
      const meetingsSummary = clientData.meetings.map((meeting, index) => {
        const conversion = meeting.contactsGenerated > 0 ? ((meeting.patients / meeting.contactsGenerated) * 100).toFixed(1) : 0;
        const objTotal = (meeting.objections?.price || 0) + (meeting.objections?.location || 0) + (meeting.objections?.noResponse || 0) + (meeting.objections?.schedule || 0);
        const servicesTotal = meeting.servicesSold?.reduce((sum, s) => sum + (s.quantity * parseFloat(s.value || 0)), 0) || 0;
        
        return `${formatDateCorrectly(meeting.date)}: ${meeting.contactsGenerated}C→${meeting.patients}P (${conversion}%), ${objTotal} objeciones, $${servicesTotal.toFixed(0)} ingresos`;
      }).join(' | ');

      const prompt = `Analiza este cliente de marketing médico y genera insights profesionales:

CLIENTE: ${clientData.name} (${clientData.specialty || 'General'})
MÉTRICAS: ${clientData.meetings.length} juntas, ${clientData.totalContacts} contactos, ${clientData.totalPatients} pacientes, ${clientData.conversionRate} conversión, $${clientData.totalRevenue || 0} ingresos

HISTORIAL JUNTAS: ${meetingsSummary}

ANÁLISIS REQUERIDO:
1. Rendimiento general y tendencias
2. Patrones en objeciones y conversiones
3. Servicios más exitosos
4. Comparación temporal entre juntas
5. Recomendaciones específicas y accionables
6. Oportunidades de mejora
7. Estrategias más efectivas

Proporciona análisis detallado y específico para este cliente.`;

      const response = await openai.chat.completions.create({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content: "Eres un analista experto en marketing digital para médicos. Proporciona análisis detallados y profesionales."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        max_tokens: 3000,
        temperature: 0.7
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Error al generar análisis con IA:', error);
      return `ANÁLISIS AUTOMÁTICO - ${clientData.name}

RENDIMIENTO GENERAL:
• ${clientData.meetings.length} juntas realizadas con ${clientData.totalContacts} contactos generados
• ${clientData.totalPatients} pacientes concretados (${clientData.conversionRate} tasa de conversión)
• Ingresos totales: $${clientData.totalRevenue || 0}
• Especialidad: ${clientData.specialty || 'No especificada'}

TENDENCIAS IDENTIFICADAS:
• Promedio de contactos por junta: ${clientData.meetings.length > 0 ? (clientData.totalContacts / clientData.meetings.length).toFixed(1) : '0'}
• Promedio de pacientes por junta: ${clientData.meetings.length > 0 ? (clientData.totalPatients / clientData.meetings.length).toFixed(1) : '0'}
• Rendimiento ${parseFloat(clientData.conversionRate) > 50 ? 'satisfactorio' : 'requiere optimización'}

RECOMENDACIONES:
• Continuar con el seguimiento regular de métricas
• ${parseFloat(clientData.conversionRate) < 50 ? 'Optimizar estrategias de conversión' : 'Mantener estrategias actuales'}
• Programar revisiones periódicas para evaluar progreso`;
    }
  };

  // Función para generar minuta ejecutiva con OpenAI - TODOS LOS DATOS
  const generateExecutiveSummary = async (clientData) => {
    try {
      // Crear resumen conciso para optimizar tokens
      const recentMeetings = clientData.meetings.slice(-3).map(meeting => {
        const conversion = meeting.contactsGenerated > 0 ? ((meeting.patients / meeting.contactsGenerated) * 100).toFixed(1) : 0;
        return `${formatDateCorrectly(meeting.date)}: ${meeting.contactsGenerated}C→${meeting.patients}P (${conversion}%)`;
      }).join(' | ');

      const totalObjections = clientData.meetings.reduce((sum, meeting) => {
        if (meeting.objections) {
          return sum + (meeting.objections.price || 0) + (meeting.objections.location || 0) + (meeting.objections.noResponse || 0) + (meeting.objections.schedule || 0);
        }
        return sum;
      }, 0);

      const prompt = `Genera minuta ejecutiva profesional para:

CLIENTE: ${clientData.name} (${clientData.specialty || 'General'})
EJECUTIVO: ${clientData.salesExecutive || 'No asignado'}
PERÍODO: ${clientData.meetings.length > 0 ? `${formatDateCorrectly(clientData.meetings[clientData.meetings.length - 1].date)} - ${formatDateCorrectly(clientData.meetings[0].date)}` : 'Sin datos'}

RESULTADOS CONSOLIDADOS:
${clientData.meetings.length} juntas, ${clientData.totalContacts} contactos, ${clientData.totalPatients} pacientes, ${clientData.conversionRate} conversión, $${clientData.totalRevenue || 0} ingresos, ${totalObjections} objeciones totales

JUNTAS RECIENTES: ${recentMeetings}

MINUTA EJECUTIVA REQUERIDA:
1. Resumen ejecutivo de resultados clave
2. Estado actual del cliente y rendimiento
3. Principales logros y desafíos
4. Tendencias y patrones identificados
5. Recomendaciones estratégicas específicas
6. Próximos pasos y acciones
7. Riesgos y oportunidades

Enfócate en información crítica para decisiones estratégicas.`;

      const response = await openai.chat.completions.create({
        model: "gpt-4o-mini",
        messages: [
          {
            role: "system",
            content: "Eres un ejecutivo de cuentas experto que genera minutas ejecutivas profesionales para clientes de marketing digital médico."
          },
          {
            role: "user",
            content: prompt
          }
        ],
        max_tokens: 2000,
        temperature: 0.6
      });

      return response.choices[0].message.content;
    } catch (error) {
      console.error('Error al generar minuta ejecutiva con IA:', error);
      return `MINUTA EJECUTIVA - ${clientData.name}

RESUMEN EJECUTIVO:
• ${clientData.meetings.length} juntas realizadas con ${clientData.totalContacts} contactos generados
• ${clientData.totalPatients} pacientes concretados (${clientData.conversionRate} tasa de conversión)
• Ingresos totales: $${clientData.totalRevenue || 0}
• Especialidad: ${clientData.specialty || 'No especificada'}
• Ejecutivo asignado: ${clientData.salesExecutive || 'No asignado'}

ESTADO ACTUAL:
Cliente activo en especialidad de ${clientData.specialty || 'medicina general'} con rendimiento ${parseFloat(clientData.conversionRate) > 50 ? 'satisfactorio' : 'que requiere optimización'}.

TENDENCIAS IDENTIFICADAS:
• Promedio de contactos por junta: ${clientData.meetings.length > 0 ? (clientData.totalContacts / clientData.meetings.length).toFixed(1) : '0'}
• Promedio de pacientes por junta: ${clientData.meetings.length > 0 ? (clientData.totalPatients / clientData.meetings.length).toFixed(1) : '0'}
• Rendimiento general: ${parseFloat(clientData.conversionRate) > 50 ? 'Satisfactorio' : 'Requiere optimización'}

RECOMENDACIONES ESTRATÉGICAS:
• Continuar con el seguimiento regular de métricas de rendimiento
• ${parseFloat(clientData.conversionRate) < 50 ? 'Implementar estrategias de optimización de conversión' : 'Mantener estrategias actuales que están funcionando'}
• Programar revisiones periódicas para evaluar progreso y ajustar estrategias

PRÓXIMOS PASOS:
• Monitorear métricas de conversión en próximas juntas
• Evaluar efectividad de estrategias implementadas
• Mantener comunicación regular con el cliente`;
    }
  };

  const generateProfessionalPDF = () => {
    try {
      setIsGeneratingPDF(true);
      
      // Ocultar temporalmente los botones para la impresión
      const buttonsToHide = document.querySelectorAll('#client-profile-content button');
      const originalDisplayValues = [];
      
      // Guardar los valores originales y ocultar los botones
      buttonsToHide.forEach(button => {
        originalDisplayValues.push(button.style.display);
        button.style.display = 'none';
      });
      
      // Agregar un estilo temporal para la impresión
      const style = document.createElement('style');
      style.id = 'print-style';
      style.innerHTML = `
        @media print {
          body * {
            visibility: hidden;
          }
          #client-profile-content, #client-profile-content * {
            visibility: visible;
          }
          #client-profile-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          @page {
            size: auto;
            margin: 10mm;
          }
        }
      `;
      document.head.appendChild(style);
      
      // Imprimir la página
      window.print();
      
      // Restaurar los botones después de la impresión
      setTimeout(() => {
        buttonsToHide.forEach((button, index) => {
          button.style.display = originalDisplayValues[index];
        });
        
        // Eliminar el estilo temporal
        const printStyle = document.getElementById('print-style');
        if (printStyle) {
          printStyle.remove();
        }
        
        setIsGeneratingPDF(false);
      }, 1000);
    } catch (error) {
      console.error('Error al imprimir:', error);
      alert('Error al imprimir. Por favor, inténtalo de nuevo.');
      setIsGeneratingPDF(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <button 
            onClick={onBack}
            className="text-[#63666A] hover:text-[#0061A8] flex items-center"
          >
            <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Volver
          </button>
          <div className="flex space-x-2">
            <button
              onClick={generateProfessionalPDF}
              disabled={isGeneratingPDF}
              className="bg-[#0061A8] text-[#FFFFFF] py-1 px-3 rounded-md text-sm hover:bg-opacity-90 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isGeneratingPDF ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Generando PDF...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Generar PDF
                </>
              )}
            </button>
          </div>
        </div>
        
        <div id="client-profile-content">
          <div className="flex justify-between items-start">
            <div>
              {isEditingName ? (
                <div className="flex items-center space-x-2">
                  <input
                    type="text"
                    value={newClientName}
                    onChange={(e) => setNewClientName(e.target.value)}
                    className="text-2xl font-bold text-[#0061A8] border-b border-[#0061A8] focus:outline-none"
                  />
                  {canEdit && (
                    <>
                      <button
                        onClick={handleSaveClientName}
                        className="text-sm bg-[#A8D08D] text-[#FFFFFF] py-1 px-2 rounded-md hover:bg-opacity-90 transition-colors"
                      >
                        Guardar
                      </button>
                      <button
                        onClick={() => {
                          setIsEditingName(false);
                          setNewClientName(client.name);
                        }}
                        className="text-sm text-[#63666A] py-1 px-2 rounded-md hover:bg-[#D1D3D4] transition-colors"
                      >
                        Cancelar
                      </button>
                    </>
                  )}
                </div>
              ) : (
                <h2 className="text-2xl font-bold text-[#0061A8] flex items-center">
                  {client.name}
                  {canEdit && (
                    <button
                      onClick={() => setIsEditingName(true)}
                      className="ml-2 text-[#63666A] hover:text-[#0061A8]"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                  )}
                </h2>
              )}
              {client.specialty && <p className="text-[#63666A]">Especialidad: {client.specialty}</p>}
              {client.cell && <p className="text-[#63666A]">Célula: {client.cell}</p>}
            </div>
            <div className="text-right">
              <p className="text-sm text-[#63666A]">Total juntas: {client.meetings.length}</p>
              <div className="flex flex-col space-y-2 mt-2">
                <button
                  onClick={onNewMeeting}
                  className="bg-[#0061A8] text-[#FFFFFF] py-1 px-3 rounded-md text-sm hover:bg-opacity-90 transition-colors"
                >
                  Nueva Junta
                </button>
                {lastMeeting && (
                  <button
                    onClick={() => onEditMeeting(lastMeeting)}
                    className="bg-[#63666A] text-[#FFFFFF] py-1 px-3 rounded-md text-sm hover:bg-opacity-90 transition-colors"
                  >
                    Editar Última Minuta
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Botones para mostrar secciones de detalles y tareas */}
          <div className="mt-6 flex space-x-2">
            <button
              onClick={() => setShowClientDetails(true)}
              className="bg-[#0061A8] text-[#FFFFFF] py-2 px-4 rounded-md text-sm hover:bg-opacity-90 transition-colors"
            >
              Ver Detalles del Cliente
            </button>
            <button
              onClick={() => setShowClientTasks(true)}
              className="bg-[#63666A] text-[#FFFFFF] py-2 px-4 rounded-md text-sm hover:bg-opacity-90 transition-colors"
            >
              Ver Tareas
            </button>
          </div>

          {/* Métricas principales - PRIMERO */}
          <div className="mt-6 bg-[#F8F9FA] p-4 rounded-lg">
            <h3 className="text-lg font-semibold text-[#0061A8] mb-4">Métricas Principales</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-[#63666A] text-sm">Contactos Generados</p>
                <div className="flex items-center mt-1">
                  <div className="h-4 bg-[#0061A8] rounded-full" style={{ width: `${contactsWidth}%` }}></div>
                  <span className="ml-2 text-lg font-semibold">{totalContacts}</span>
                </div>
              </div>
              <div>
                <p className="text-[#63666A] text-sm">Pacientes Concretados</p>
                <div className="flex items-center mt-1">
                  <div className="h-4 bg-[#A8D08D] rounded-full" style={{ width: `${patientsWidth}%` }}></div>
                  <span className="ml-2 text-lg font-semibold">{totalPatients}</span>
                </div>
              </div>
              <div>
                <p className="text-[#63666A] text-sm">Tasa de Conversión</p>
                <div className="flex items-center mt-1">
                  <div className="h-4 bg-[#F4B183] rounded-full" style={{ width: `${conversionWidth}%` }}></div>
                  <span className="ml-2 text-lg font-semibold">{conversionRate}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Línea de Tiempo (Historial de juntas) - SEGUNDO */}
          <div className="mt-6">
            <h3 className="text-lg font-semibold text-[#0061A8] mb-4">Línea de Tiempo</h3>
            <ClientTimeline meetings={client.meetings} onViewSummary={handleViewSummary} />
          </div>

          {/* Gráficas - TERCERO */}
          <div className="mt-6">
            <ClientCharts client={client} />
          </div>
        </div>
      </div>

      {/* Modal para ver resumen de junta */}
      {showSummaryModal && selectedMeetingForSummary && (
        <MeetingSummaryModal
          meeting={selectedMeetingForSummary}
          onClose={() => setShowSummaryModal(false)}
          onEdit={canEdit ? () => {
            setShowSummaryModal(false);
            onEditMeeting(selectedMeetingForSummary);
          } : undefined}
        />
      )}

      {/* Sección de detalles del cliente */}
      {showClientDetails && (
        <ClientDetailsSection
          client={client}
          onClose={() => setShowClientDetails(false)}
          onUpdate={canEdit ? onUpdateClient : undefined}
        />
      )}

      {/* Sección de tareas del cliente */}
      {showClientTasks && (
        <ClientTasksSection
          client={client}
          onClose={() => setShowClientTasks(false)}
        />
      )}
    </div>
  );
};

export default ClientProfile;
